import * as React from "react";
import { Input } from "../../shared/Input";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorAlert } from "../../shared/ErrorAlert";
import { InputError } from "../../shared/InputError";
import cx from "classnames";
import {
  AuthSuccess,
  signupWithEmail,
  VerifySuccess,
  verifyUserByEmail,
} from "../auth/AuthApi";
import { ErrorDetails } from "../../shared/Http";
import styles from "./Trial.module.css";
import { GetFBPcookie } from "../../shared/SegmentAnalytics";
import { FireOptimizelyEvent } from "../../shared/Optimizely";
import * as analyticsApi from "../analytics/AnalyticsApi";
import {
  Cart,
  CatalogGroups,
  CheckoutState,
  useStoreToken,
  useCatalog,
  getProducts,
  addToCart,
} from "../store/StoreApi";
import { useNavigate } from "react-router-dom";
import { VideoBackground } from "../../shared/VideoBackground";
import Logo from "../../shared/Logo";

const TRIAL_FORM_MAX_STEP = 2;

const TRIAL_BUTTON_CLASSES =
  "uppercase mt-3 w-full text-gray-100 font-semibold text-lg flex justify-center py-2 px-4 border border-transparent rounded-sm shadow-sm bg-lb-green hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lb-green";

type TrialFormData = {
  email: string;
  displayName: string;
  acceptsMarketing: boolean;
  fbp: string;
};

type TrialExistingMemberFormData = {
  email: string;
};

type TrialProps = {
  isVariation: boolean;
};

export const Trial = ({ isVariation }: TrialProps) => {
  const [currentStep, setStep] = React.useState(0);
  const [email, setEmail] = React.useState<string>("");
  const [acceptsMarketing, setAcceptsMarketing] = React.useState<
    boolean | undefined
  >(undefined);

  const token = useStoreToken();
  const { data } = useCatalog(token, CatalogGroups.VR);
  const products = getProducts(data);
  let productId = 0;
  if (typeof products !== "undefined") {
    if (products.length === 1) {
      productId = products[0].id!;
    }
  }
  const navigate = useNavigate();
  const goToCheckout = () => {
    addToCart(token, 0, productId).then((cart: Cart) => {
      const state: CheckoutState = {
        cart: cart,
        email: email,
        createAccount: false,
        acceptsMarketing: acceptsMarketing,
        sourceGroup: CatalogGroups.VR,
      };

      navigate(`/checkout`, {
        state: state,
      });
    });
  };

  React.useEffect(() => {
    if (window.analytics) {
      window.analytics.page("Members - VR Trial", {
        source: "memberPortal",
        fbp: GetFBPcookie(),
      });
    }
  }, []);

  const FormInputHandler = (
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };
  };

  const Step0Form = () => {
    // We have to use locally bound state lest the component re-render on every keystroke.
    const [localEmail, setLocalEmail] = React.useState<string>(email);
    const handleEmailChange = FormInputHandler(setLocalEmail);
    const handleReturn = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        nextStepSetEmail(localEmail);
      }
    };
    return (
      <>
        <div className="flex">
          <div className="w-5/6 mx-auto">
            <h2 className="uppercase font-black tracking-wide text-3xl text-center text-white mt-6">
              {isVariation
                ? "Join Liteboxer. $9.99/mo for 3 Months."
                : "Join Liteboxer. $9.99/mo for 3 Months."}
            </h2>
            <h4 className="text-lg leading-7 text-white mt-4 mb-10 text-center">
              Real trainers, unlimited workouts, hit music, and more.
              <br className="md:hidden" /> Cancel anytime.
            </h4>
          </div>
        </div>
        <div className="flex">
          <div className="w-5/6 mx-auto">
            <Input
              isError={false}
              label={"Email"}
              placeholder={"Enter your email"}
              id={"email"}
              onChange={handleEmailChange}
              onKeyDown={handleReturn}
              value={localEmail}
              key="step0email"
              className="bg-opacity-75 bg-white"
            />
          </div>
        </div>
        <div className="w-64 mx-auto">
          <button
            className={cx(
              TRIAL_BUTTON_CLASSES,
              "mt-10 mb-10 tracking-wider py-3"
            )}
            onClick={() => nextStepSetEmail(localEmail)}
          >
            Get Started
          </button>
        </div>
        <div className="text-center italic text-white text-xs mt-6">
          * Meta Quest headset sold separately via Meta. Offer valid for new
          users only.
        </div>
      </>
    );
  };

  const Step1Form = () => {
    const initialFormValues = {
      email: email,
      password: "",
      displayName: "",
      acceptsMarketing: true,
      fbp: GetFBPcookie(),
    };
    const {
      register,
      handleSubmit,
      setError,
      formState: { errors },
    } = useForm<TrialFormData>({
      defaultValues: { ...initialFormValues },
    });
    const [globalError, setGlobalError] = React.useState<undefined | string>(
      undefined
    );

    const onSubmit: SubmitHandler<TrialFormData> = (data: TrialFormData) => {
      setAcceptsMarketing(data.acceptsMarketing);
      return signupWithEmail(
        data.email,
        "lite",
        "boxer",
        data.displayName,
        data.acceptsMarketing,
        GetFBPcookie(),
        "VR"
      ).then((response) => {
        if (response.ok) {
          response.json().then((success: AuthSuccess) => {
            if (window.analytics) {
              window.analytics.identify(success.uuid, {
                firstName: success.firstName,
                lastName: success.lastName,
                displayName: success.displayName,
                email: data.email,
                source: "memberPortal",
                acceptsMarketing: data.acceptsMarketing,
                fbp: GetFBPcookie(),
              });
            }

            nextStep();
          });
        } else if (response.status === 400) {
          response.json().then((errorDetails: ErrorDetails<TrialFormData>) => {
            if (errorDetails.error.displayName) {
              setError("displayName", {
                type: "server",
                message: errorDetails.error.displayName,
              });
            } else {
              setGlobalError(errorDetails.status);
            }
          });
        } else {
          setGlobalError(
            "Could not create a new account. Please contact support@liteboxer.com"
          );
        }
      });
    };

    const validDisplayName = (d: string): boolean =>
      d.length >= 3 && d.length <= 15 && d.match(/^[a-zA-Z0-9_.-]+$/) !== null;
    return (
      <>
        <div className="text-center uppercase text-lb-green font-extrabold mb-3 mt-12">
          Step 2 of 3
        </div>
        <div className="flex">
          <div className="w-5/6 mx-auto">
            <h2 className="uppercase font-black tracking-wide text-4xl text-center text-white mt-6">
              Join today for just $18.99/mo*
            </h2>
            <h4 className="text-md leading-7 text-white mt-6 mb-6 text-center">
              Choose a username to create your Liteboxer account.
              <br className="hidden md:block" /> Usernames must be 3-15
              characters.
            </h4>
          </div>
        </div>

        {globalError ? (
          <ErrorAlert
            heading={globalError}
            onClose={() => {
              setGlobalError(undefined);
            }}
          />
        ) : null}

        <div className="flex">
          <div className="w-5/6 mx-auto">
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className={"mt-5"}>
                <Input
                  isError={!!errors.email}
                  label={"Email"}
                  showLabel={true}
                  id={"email"}
                  className="bg-black text-white border-0 border-b"
                  {...register("email", { required: true })}
                />
              </div>
              {errors.email && errors.email.type === "required" && (
                <InputError id="email-error">This field is required</InputError>
              )}
              {errors.email && errors.email.type === "server" && (
                <InputError id="email-error">
                  {errors.email.message ?? "Unknown error"}
                </InputError>
              )}

              <div className={"mt-5"}>
                <Input
                  isError={!!errors.displayName}
                  label={"Username"}
                  showLabel={true}
                  id={"displayName"}
                  className="bg-black text-white border-0 border-b"
                  {...register("displayName", {
                    required: true,
                    validate: {
                      server: (v: string) =>
                        validDisplayName(v) ||
                        "Your username must be between 3 and 15 letters, numbers, dots, or dashes",
                    },
                  })}
                />
              </div>
              {errors.displayName && errors.displayName.type === "required" && (
                <InputError id="displayName-error">
                  This field is required
                </InputError>
              )}
              {errors.displayName && errors.displayName.type === "server" && (
                <InputError id="displayName-error">
                  {errors.displayName.message ?? "Unknown error"}
                </InputError>
              )}

              <div className={"mt-6"}>
                <div className="flex">
                  <input
                    id="marketing"
                    type="checkbox"
                    {...register("acceptsMarketing")}
                    className={cx(
                      "h-4 w-4 m-1 text-xs border-gray-300 rounded focus:ring-lb-green text-lb-green flex-initial"
                    )}
                  />
                  <label
                    htmlFor="marketing"
                    className="text-xs pl-1.5 leading-3 text-gray-700 inline-block flex-1"
                  >
                    I would like to receive email communication about Liteboxer
                    news and product updates.
                  </label>
                </div>
              </div>
              <div className="w-64 mx-auto">
                <button
                  className={cx(
                    TRIAL_BUTTON_CLASSES,
                    "mt-6 tracking-wider py-3"
                  )}
                >
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="text-center italic text-white text-xs mt-6">
          * Meta Quest headset sold separately via Meta.
        </div>
      </>
    );
  };

  const Step2Form = () => {
    return (
      <>
        <div className="text-center uppercase text-lb-green font-extrabold mb-3 mt-12">
          Step 3 of 3
        </div>
        <div className="flex">
          <div className="w-full mx-auto">
            <h2 className="uppercase font-black tracking-wide text-4xl text-center text-white mt-6">
              Jump Into <br className="md:hidden" /> Your Headset
            </h2>
          </div>
        </div>
        <div className="px-2">
          <div className="flex">
            <div className="w-5/6 mx-auto">
              <h4 className="text-md text-white leading-7 mt-6 text-center">
                Install Liteboxer on your Meta Quest and sign in with the
                account you created to get started.
              </h4>
            </div>
          </div>

          <ul className="list-none text-white text-sm mt-6">
            <li className="my-7">
              <div className="flex">
                <div className="flex-initial">
                  <svg
                    width="37"
                    height="37"
                    viewBox="0 0 37 37"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="18.5" cy="18.5" r="18.5" fill="#D9D9D9" />
                    <g clip-path="url(#clip0_54_29)">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M13.9374 11.5839C16.7534 11.561 19.5695 11.5839 22.3856 11.5839C23.3361 11.5839 24.2867 11.6412 25.2016 11.8931C27.1622 12.4198 28.8613 13.7938 29.7287 15.5686C30.5961 17.3091 30.6437 19.3931 29.8594 21.1679C29.1109 22.9083 27.5781 24.3167 25.7482 24.958C24.7145 25.3358 23.5976 25.4389 22.5044 25.4389C19.7715 25.4389 17.0505 25.4389 14.3176 25.4389C13.1294 25.4389 11.9293 25.2671 10.848 24.7977C9.32712 24.1564 8.05573 22.9885 7.33092 21.5457C6.53482 19.9999 6.38035 18.1564 6.91504 16.5076C7.33092 15.1908 8.16267 13.9999 9.27959 13.1526C10.1826 12.4427 11.2758 11.9732 12.4165 11.7557C12.9274 11.6755 13.4383 11.6068 13.9493 11.5954L13.9374 11.5839ZM12.4046 16.645C11.9531 16.9427 11.6204 17.3893 11.4659 17.8931C11.2995 18.477 11.3708 19.1297 11.7035 19.6564C12.0957 20.3091 12.8561 20.7213 13.6284 20.79H23.4074C23.9659 20.7099 24.5125 20.5038 24.9046 20.1144C25.2611 19.7824 25.4987 19.3473 25.57 18.8778C25.7126 18.1335 25.4155 17.332 24.8214 16.8282C24.3105 16.3702 23.5976 16.1984 22.9203 16.1984C19.9973 16.1984 17.0624 16.1984 14.1394 16.1984C13.5334 16.1984 12.9155 16.3129 12.4046 16.645V16.645Z"
                        stroke="black"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_54_29">
                        <rect
                          width="25"
                          height="15"
                          fill="white"
                          transform="translate(6 11)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="flex-auto ml-2 my-auto">
                  Install the app on your Meta Quest device using your phone or
                  headset.
                </div>
              </div>
            </li>
            <li className="my-7">
              <div className="flex">
                <div className="flex-initial">
                  <svg
                    width="37"
                    height="37"
                    viewBox="0 0 37 37"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="18.5" cy="18.5" r="18.5" fill="#D9D9D9" />
                    <g clip-path="url(#clip0_54_30)">
                      <path
                        d="M5.29477 18.4135L5.05681 18.3988L5.29477 18.4135ZM12.3195 19.0791L12.4242 18.8588L12.3195 19.0791ZM14.5706 23.2435H14.3327V23.2631L14.5706 23.2435ZM13.7996 27.54L13.9329 27.7407L13.7996 27.54ZM7.05571 25.196L6.89865 25.3771L7.05571 25.196ZM10.0065 16.1087L9.97791 15.864H9.9684L10.0065 16.1087ZM12.5765 16.2408L12.6431 16.0059L12.5765 16.2408ZM28.5487 14.5868L28.601 14.347L28.482 14.8217L28.5487 14.5868ZM30.0574 16.505L30.1811 16.7155L30.0574 16.505ZM18.6827 19.8767L18.7302 20.1165L18.6827 19.8767ZM18.359 23.5126L18.4066 23.7524H18.4257L18.359 23.5126ZM28.3202 20.4737L28.3868 20.7086H28.3964L28.3155 20.4737H28.3202ZM30.7617 18.624L30.9997 18.6386V18.624H30.7617ZM30.7617 15.9814L30.5238 15.9619V15.9814H30.7617ZM26.5212 16.8427L26.7354 16.735L26.5212 16.8427ZM26.7163 13.7353L26.9543 13.7549L26.7163 13.7353ZM20.0295 8.37683L20.0581 8.6215H20.0676L20.0295 8.37683ZM28.5772 13.9261L28.8152 13.9506V13.9408L28.5772 13.9261ZM21.5715 8.57746L21.6191 8.81725H21.6287L21.5715 8.57746ZM15.9175 12.9376L15.7509 12.7664L15.7224 12.7957L15.7033 12.83L15.9175 12.9376ZM14.247 12.0764L14.2803 11.8317H14.247V12.0764ZM5.53274 18.4282C5.54226 18.2325 5.62316 17.7676 5.84209 17.4201C5.95155 17.2489 6.08006 17.1216 6.24663 17.0531C6.40369 16.9846 6.61786 16.9699 6.91293 17.0678L7.06523 16.6029C6.68449 16.4708 6.35134 16.4806 6.07054 16.6029C5.7945 16.7204 5.58985 16.9308 5.44707 17.1559C5.16627 17.6012 5.07585 18.1591 5.06157 18.4037L5.5375 18.4282H5.53274ZM6.90817 17.0678C8.25505 17.5278 9.91128 18.1199 12.21 19.2993L12.4242 18.8637C10.0969 17.6648 8.41211 17.0678 7.06047 16.6029L6.90817 17.0678ZM12.21 19.2993C13.0715 19.7446 13.6045 20.4835 13.9234 21.242C14.2422 22.0054 14.3374 22.7737 14.3279 23.2386L14.8038 23.2484C14.8134 22.7199 14.7086 21.8831 14.3612 21.0512C14.0138 20.2144 13.4094 19.3727 12.4242 18.8637L12.21 19.2993ZM14.3279 23.2631C14.3707 23.8454 14.3803 24.7605 14.2851 25.5924C14.2375 26.0083 14.1613 26.3949 14.0519 26.7081C13.9424 27.0262 13.8044 27.2366 13.6616 27.3394L13.9329 27.7407C14.1994 27.5498 14.3803 27.2268 14.504 26.8745C14.6277 26.5124 14.7134 26.0866 14.761 25.6511C14.8657 24.7801 14.8514 23.8356 14.8086 23.2288L14.3327 23.2631H14.3279ZM13.6568 27.3394C13.4712 27.4715 13.1143 27.54 12.605 27.496C12.1053 27.4568 11.5056 27.3198 10.8631 27.0996C9.57813 26.6592 8.18366 25.9105 7.20801 25.0101L6.88913 25.3722C7.9219 26.3264 9.383 27.1094 10.7108 27.5596C11.3771 27.7847 12.0196 27.9364 12.567 27.9756C13.1048 28.0196 13.5997 27.9658 13.9234 27.7309L13.6521 27.3296L13.6568 27.3394ZM7.20801 25.0149C6.23711 24.1194 5.80878 23.5713 5.6184 22.7345C5.41851 21.8586 5.46611 20.6597 5.52798 18.4282L5.05205 18.4135C4.99018 20.6058 4.93307 21.8929 5.15199 22.8471C5.38044 23.8405 5.8992 24.4669 6.88913 25.382L7.20801 25.0198V25.0149ZM6.26567 17.014L10.0445 16.3533L9.96364 15.8689L6.18476 16.5295L6.26567 17.014ZM10.0303 16.3533C10.1064 16.3436 10.4967 16.3044 10.9821 16.3044C11.4723 16.3044 12.0482 16.3436 12.5003 16.4806L12.6336 16.0108C12.1101 15.8542 11.4866 15.815 10.9774 15.815C10.4681 15.815 10.0588 15.8591 9.96364 15.8689L10.0207 16.3533H10.0303ZM12.5051 16.4757C12.7859 16.5589 13.3332 16.6812 14.0043 16.8427C14.6801 17.0042 15.4939 17.2097 16.3316 17.4593C17.1692 17.7089 18.0259 18.0074 18.7874 18.3499C19.5488 18.6974 20.2009 19.0888 20.6387 19.5195L20.9671 19.1671C20.4721 18.6778 19.763 18.2569 18.9825 17.8997C18.1972 17.5425 17.3167 17.2391 16.4696 16.9846C15.6177 16.7301 14.7943 16.5246 14.1185 16.3631C13.4332 16.1967 12.9096 16.0842 12.6479 16.001L12.5146 16.4708L12.5051 16.4757ZM28.4773 14.8217C28.9389 14.9636 29.6005 15.2327 29.986 15.5459C30.1859 15.7074 30.2525 15.8346 30.2525 15.9178C30.2525 15.9765 30.2239 16.1087 29.9289 16.2946L30.1716 16.7155C30.519 16.5001 30.7379 16.231 30.7284 15.9031C30.7189 15.5948 30.5047 15.3502 30.281 15.1691C29.8242 14.7923 29.0912 14.5036 28.6105 14.3568L28.4773 14.8265V14.8217ZM29.9289 16.2946C29.1579 16.7693 27.5635 17.3565 25.5265 17.9535C23.4991 18.5457 21.0671 19.1378 18.6303 19.632L18.7207 20.1116C21.167 19.6173 23.618 19.0203 25.655 18.4233C27.6872 17.8312 29.3387 17.2293 30.1716 16.7106L29.9289 16.2897V16.2946ZM18.6303 19.632C18.1211 19.7348 17.3643 19.9795 16.7742 20.3856C16.184 20.7869 15.6843 21.4035 15.8747 22.2403L16.3363 22.1326C16.2173 21.5943 16.5077 21.1539 17.0359 20.7918C17.5595 20.4346 18.2448 20.2095 18.7207 20.1116L18.6303 19.632ZM15.8747 22.2403C16.0508 23.0135 16.5172 23.4392 17.0312 23.6399C17.5309 23.8356 18.0639 23.8209 18.4066 23.7475L18.3114 23.2679C18.0354 23.3267 17.5975 23.3365 17.2025 23.1848C16.8218 23.0331 16.4743 22.7297 16.3411 22.1326L15.8794 22.2403H15.8747ZM18.4257 23.7426L28.3868 20.7037L28.2536 20.2339L18.2924 23.2728L18.4257 23.7426ZM28.3964 20.6988C28.8057 20.552 29.4196 20.278 29.9431 19.9403C30.2049 19.769 30.4524 19.5782 30.6428 19.3678C30.8284 19.1622 30.9807 18.9127 30.9997 18.6337L30.5238 18.5995C30.5143 18.7169 30.4476 18.8637 30.2953 19.035C30.143 19.2014 29.9336 19.3678 29.6909 19.5244C29.2054 19.8376 28.6248 20.0969 28.2393 20.2339L28.3964 20.6939V20.6988ZM30.9997 18.6191V15.9765H30.5238V18.6191H30.9997ZM30.9997 15.9961C31.014 15.8346 30.9331 15.678 30.8426 15.5459C30.7427 15.404 30.5952 15.2572 30.4 15.1153C30.0145 14.8265 29.4244 14.5427 28.601 14.3519L28.4963 14.8314C29.2673 15.0125 29.7956 15.2768 30.124 15.5165C30.2858 15.6389 30.3953 15.7514 30.4619 15.8395C30.5333 15.9374 30.5285 15.9765 30.5285 15.9619L31.0045 16.001L30.9997 15.9961ZM28.1727 17.4397C28.1727 17.3369 28.1156 17.2733 28.1013 17.2635C28.0823 17.244 28.0585 17.2293 28.0489 17.2195C28.0251 17.2048 27.9966 17.1901 27.9775 17.1804C27.93 17.1608 27.8728 17.1363 27.8062 17.1069C27.6777 17.058 27.5016 16.9944 27.3303 16.9308C27.159 16.8672 26.9876 16.8035 26.8591 16.7497C26.7925 16.7204 26.7449 16.7008 26.7116 16.6812L26.6925 16.6714H26.6973C26.6973 16.6714 26.7163 16.7008 26.7306 16.7252L26.307 16.9455C26.3356 17.0091 26.3832 17.0433 26.4022 17.058C26.4308 17.0776 26.4593 17.0972 26.4831 17.1118C26.5355 17.1412 26.6021 17.1706 26.6735 17.1999C26.8163 17.2586 26.9971 17.3272 27.1685 17.3908C27.3446 17.4544 27.5111 17.5131 27.6349 17.562C27.6968 17.5865 27.7443 17.6061 27.7729 17.6208C27.7872 17.6257 27.7919 17.6306 27.7872 17.6257C27.7872 17.6257 27.7729 17.6159 27.7586 17.6012C27.7539 17.5963 27.6968 17.5376 27.6968 17.4397H28.1727ZM26.7354 16.7252C26.7354 16.7252 26.7497 16.7546 26.7497 16.7644C26.7497 16.7693 26.7497 16.7742 26.7497 16.7791C26.7497 16.7693 26.7497 16.7448 26.7497 16.7155C26.7497 16.6567 26.7497 16.5687 26.7544 16.461C26.7639 16.2457 26.783 15.957 26.802 15.6389C26.8448 15.0027 26.9067 14.254 26.9495 13.7597L26.4736 13.7157C26.4308 14.21 26.3689 14.9636 26.3261 15.6046C26.3023 15.9227 26.288 16.2212 26.2785 16.4414C26.2785 16.554 26.2737 16.647 26.2737 16.7204C26.2737 16.7546 26.2737 16.7889 26.2737 16.8182C26.2737 16.8329 26.2737 16.8476 26.2785 16.8672C26.2785 16.8818 26.288 16.9112 26.3023 16.9504L26.7259 16.7301L26.7354 16.7252ZM20.001 8.13704C19.5441 8.19576 18.459 8.3964 17.2691 8.96405C16.0793 9.53171 14.7753 10.4664 13.9091 11.9932L14.3184 12.2378C15.1227 10.8236 16.3363 9.94766 17.4643 9.40937C18.597 8.87108 19.6298 8.67533 20.0533 8.6264L19.9962 8.14194L20.001 8.13704ZM28.4868 17.3516L28.8152 13.9555L28.3393 13.9066L28.0109 17.3027L28.4868 17.3516ZM28.8152 13.9457C28.8818 12.7517 28.3678 11.0194 27.1827 9.74702C25.9834 8.46002 24.113 7.65747 21.5097 8.34257L21.6287 8.81725C24.0654 8.17619 25.7597 8.9298 26.8401 10.0896C27.9347 11.264 28.4011 12.8642 28.3393 13.9212L28.8152 13.9506V13.9457ZM21.524 8.33768C20.891 8.4747 19.8344 8.89065 18.7398 9.58554C17.6451 10.2804 16.4934 11.2591 15.6986 12.5413L16.0984 12.8055C16.8456 11.6017 17.9307 10.667 18.9872 10.0015C20.0438 9.33107 21.0528 8.93959 21.6191 8.81725L21.5192 8.33768H21.524ZM14.3327 16.7301L16.1317 13.0453L15.7081 12.8251L13.9091 16.5099L14.3327 16.7301ZM16.0888 13.1089C16.184 13.011 16.2364 12.8544 16.1745 12.6783C16.1222 12.5168 15.9841 12.3798 15.7795 12.2623L15.5415 12.6881C15.6177 12.7321 15.6653 12.7712 15.6891 12.8006C15.7176 12.83 15.7224 12.8447 15.7224 12.8447C15.7224 12.8349 15.7224 12.8006 15.7462 12.7664L16.0841 13.1138L16.0888 13.1089ZM15.7795 12.2623C15.5701 12.14 15.2702 12.0421 14.9942 11.9736C14.7182 11.9051 14.4469 11.8562 14.2756 11.8317L14.2137 12.3161C14.366 12.3357 14.623 12.3798 14.88 12.4483C15.1465 12.5168 15.3892 12.5951 15.5415 12.6832L15.7795 12.2574V12.2623ZM14.247 11.8317C14.1233 11.8317 13.9805 11.8904 13.8377 11.9981C13.6902 12.1106 13.5379 12.2868 13.3951 12.551L13.8091 12.7908C13.9234 12.5804 14.0376 12.4581 14.1233 12.3895C14.2137 12.321 14.2613 12.321 14.247 12.321V11.8317ZM13.3951 12.551C13.1381 13.0306 12.0625 15.0761 11.5961 15.9765L12.0149 16.2065C12.4861 15.3012 13.5569 13.2606 13.8139 12.7859L13.3998 12.5461L13.3951 12.551ZM26.9495 13.75C27.04 12.7174 26.6783 11.22 25.7216 10.0162C24.7508 8.79767 23.1707 7.88747 20.8481 8.03917L20.8767 8.52853C23.0469 8.38661 24.4747 9.2332 25.3504 10.3294C26.2309 11.4353 26.5498 12.8104 26.4736 13.7108L26.9495 13.7549V13.75ZM20.8481 8.03917C20.5721 8.05874 20.2865 8.08811 19.9915 8.13704L20.0676 8.6215C20.3484 8.57746 20.6197 8.54321 20.8814 8.52853L20.8529 8.03917H20.8481ZM24.1749 8.15172C22.7376 7.93641 21.2384 8.00492 20.8434 8.03917L20.8814 8.52853C21.2574 8.49917 22.7185 8.43066 24.1082 8.63619L24.1796 8.15172H24.1749ZM26.6973 13.9506C23.2611 13.5493 18.7921 13.8723 14.6468 15.1544L14.7848 15.6242C18.8683 14.3665 23.2706 14.0485 26.6449 14.44L26.6973 13.9555V13.9506ZM26.5069 16.3778C26.5069 16.3778 26.5022 16.3778 26.4689 16.3778C26.4403 16.3778 26.4022 16.3778 26.3499 16.3729C26.2499 16.3729 26.1024 16.3631 25.9215 16.3582C25.5598 16.3484 25.0506 16.3387 24.4557 16.3387C23.2611 16.3387 21.7 16.3974 20.2056 16.598L20.2675 17.0825C23.204 16.691 26.426 16.8721 26.5069 16.8721V16.3827V16.3778Z"
                        fill="black"
                      />
                      <path
                        d="M22.933 18.8246C22.6713 18.4869 22.1192 17.9486 21.391 17.4397C20.6629 16.9307 19.7491 16.4512 18.7592 16.2408L18.7306 16.3729C19.6967 16.5833 20.5915 17.0531 21.3149 17.5522C22.0335 18.0563 22.5333 18.4282 22.7855 18.7512L22.9283 18.8197L22.933 18.8246ZM18.7592 16.2408C16.8031 15.8199 15.2849 15.5361 14.7709 15.448L14.7471 15.585C15.2611 15.6731 16.7745 15.9569 18.7306 16.3778L18.7592 16.2456V16.2408Z"
                        fill="black"
                        stroke="black"
                        strokeWidth="0.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.0357 26.2187L17.9643 25.9838L18.0357 26.2187ZM23.5279 21.746C23.4803 21.9368 23.4184 23.2189 22.6665 24.3885C22.3 24.961 21.767 25.4944 20.996 25.8272C20.225 26.16 19.197 26.3019 17.8168 26.0572L17.7359 26.5417C19.1827 26.7961 20.3106 26.6542 21.1768 26.2774C22.0478 25.9006 22.6474 25.2987 23.0567 24.6576C23.466 24.0215 23.6897 23.3462 23.8135 22.8177C23.8753 22.5534 23.9134 22.3234 23.942 22.1521C23.9562 22.0689 23.9658 21.9955 23.9705 21.9417C23.98 21.8781 23.98 21.8634 23.98 21.8634L23.5184 21.746H23.5279ZM21.786 22.5926C21.786 22.5926 21.7955 22.5926 21.7765 22.6904C21.7622 22.7736 21.7241 22.8862 21.6623 23.0281C21.5385 23.3021 21.3196 23.6545 21.0007 24.0215C20.3677 24.7555 19.3588 25.5483 17.9643 25.9838L18.1023 26.4536C19.5872 25.9887 20.6723 25.1421 21.3529 24.3445C21.6956 23.9481 21.9383 23.5615 22.0858 23.2336C22.1572 23.0721 22.2096 22.9155 22.2381 22.7834C22.2619 22.6562 22.2667 22.5045 22.2048 22.3772L21.7813 22.5975L21.786 22.5926ZM17.9691 25.9838C16.1605 26.5466 15.4371 26.85 14.9469 27.0408C14.4805 27.2268 14.2425 27.3051 13.3906 27.4372L13.462 27.9217C14.3472 27.7895 14.6138 27.6965 15.1182 27.5008C15.6037 27.31 16.3128 27.0114 18.1071 26.4536L17.9691 25.9838Z"
                        fill="black"
                      />
                      <path
                        d="M21.7812 8.45509C21.1911 8.45509 19.6205 8.77317 18.0356 10.0406L18.0499 10.0602C19.63 8.79764 21.1958 8.47956 21.7764 8.47956V8.4502L21.7812 8.45509Z"
                        fill="black"
                      />
                      <path
                        d="M13.205 18.6435L13.2288 18.5994L13.205 18.6435ZM7.48438 16.6567C8.43623 16.8084 10.378 17.0629 13.1812 18.6875L13.2288 18.6043C10.4066 16.9748 8.45051 16.7154 7.49865 16.5637L7.48438 16.6616V16.6567ZM13.1812 18.6826C13.3573 18.7903 13.8047 19.172 14.2283 19.7299C14.6519 20.2877 15.0516 21.012 15.1373 21.8145L15.2325 21.8047C15.1421 20.9777 14.7328 20.2339 14.3044 19.6711C13.8761 19.1084 13.4192 18.7218 13.2288 18.6043L13.1812 18.6875V18.6826ZM15.1373 21.8096C15.3562 23.8405 15.3562 25.6364 14.4805 27.4323L14.5662 27.4764C15.4514 25.656 15.4514 23.8356 15.2325 21.7998L15.1373 21.8096Z"
                        fill="black"
                        stroke="black"
                        strokeWidth="0.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_54_30">
                        <rect
                          width="26"
                          height="20"
                          fill="white"
                          transform="translate(5 8)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="flex-auto ml-2 my-auto">
                  Put your headset on, log in with the account you created and
                  get ready to sweat!
                </div>
              </div>
            </li>
            <li className="my-7">
              <div className="flex">
                <div className="flex-initial">
                  <svg
                    width="37"
                    height="37"
                    viewBox="0 0 37 37"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="18.5" cy="18.5" r="18.5" fill="#D9D9D9" />
                    <g clip-path="url(#clip0_54_31)">
                      <path
                        d="M25.4839 21.8773C25.5923 20.7749 24.7019 19.8462 23.5948 19.8462H14.3277C14.0335 19.8462 13.7935 19.6121 13.7935 19.3252V10H12.5781V21.0392H23.5871C23.9897 21.0392 24.3071 21.3714 24.2684 21.7716C24.2374 22.1189 23.9123 22.3681 23.5561 22.3681H11.7497C11.4555 22.3681 11.2155 22.134 11.2155 21.8471V10H10V23.5536H23.5406C24.5316 23.5536 25.391 22.8362 25.4839 21.8773Z"
                        fill="black"
                      />
                      <path
                        d="M24.9961 17.6338C25.9948 16.8485 26.6297 15.6404 26.6297 14.2964C26.6297 11.9254 24.6555 10 22.2245 10H17.5948V11.1855H22.2245C23.9819 11.1855 25.4142 12.5824 25.4142 14.2964C25.4142 16.0104 23.9819 17.4073 22.2245 17.4073H16.8284C16.5342 17.4073 16.2942 17.1732 16.2942 16.8863V10H15.0787V18.5927H23.5871C25.3832 18.5927 26.8465 20.0576 26.7768 21.8244C26.7148 23.5083 25.2361 24.8145 23.5097 24.8145H10V26H23.5019C25.9019 26 27.9303 24.1727 28 21.832C28.0619 19.8839 26.7845 18.2152 25.0039 17.6338H24.9961Z"
                        fill="black"
                      />
                      <path
                        d="M22.1005 12.4464H17.5947V13.6319H22.1005C22.4799 13.6319 22.7818 13.9264 22.7818 14.2963C22.7818 14.6663 22.4721 14.9608 22.1005 14.9608H17.5947V16.1463H22.1005C23.1457 16.1463 23.9973 15.3157 23.9973 14.2963C23.9973 13.277 23.1457 12.4464 22.1005 12.4464Z"
                        fill="black"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_54_31">
                        <rect
                          width="18"
                          height="16"
                          fill="white"
                          transform="translate(10 10)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <div className="flex-auto ml-2 my-auto">
                  Jump in with premium membership for just $18.99/month
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div className="flex m-2">
          <figure className="rounded-xl mt-6 mb-6 p-3 w-full relative">
            <div className="absolute bg-lb-green top-0 right-0 rounded-tr-xl pt-2 pb-1 px-6 text-md uppercase font-bold text-white">
              Save 21%
            </div>
            <h5 className="text-lg text-lb-green font-bold mt-1">
              12 months for only $14.99/month
            </h5>
            {/* <p className="text-white text-sm mb-1">
              Take advantage of a limited time Holiday offer!
            </p> */}
          </figure>
        </div>

        <div className="w-64 mx-auto">
          {productId > 0 ? (
            <button
              className={cx(TRIAL_BUTTON_CLASSES, "mt-6 tracking-wider py-3")}
              onClick={goToCheckout}
            >
              Get Started
            </button>
          ) : (
            <button
              className={cx(TRIAL_BUTTON_CLASSES, "mt-6 tracking-wider py-3")}
              onClick={() =>
                (window.location.href =
                  "https://www.oculus.com/experiences/quest/4175028349266073/")
              }
            >
              Got It
            </button>
          )}
        </div>
      </>
    );
  };

  type CheckoutStepProps = {
    visibleStep: number;
    children: React.ReactNode;
  };

  const nextStep = () => {
    if (currentStep < TRIAL_FORM_MAX_STEP) {
      FireOptimizelyEvent(`VR Trial Form Step ${currentStep + 1}`);
      setStep(currentStep + 1);
    }
  };

  const nextStepSetEmail = (e: string) => {
    setEmail(e);

    if (window.analytics) {
      analyticsApi.identify(e).then((response) => {
        if (
          response.uuid !== null &&
          response.uuid !== "" &&
          response.email !== null &&
          response.email !== ""
        ) {
          window.analytics.identify(response.uuid, {
            email: response.email,
            source: "memberPortal",
            fbp: GetFBPcookie(),
          });
          window.analytics.track("Email Entered", {
            email: response.email,
            source: "memberPortal",
            fbp: GetFBPcookie(),
          });
        }
      });
    }

    nextStep();
  };

  const StepContent = ({ visibleStep, children }: CheckoutStepProps) => {
    if (currentStep !== visibleStep) {
      return <></>;
    }

    return <>{children}</>;
  };

  return (
    <div
      className={cx(
        "min-h-screen bg-black relative",
        styles.container,
        `step-${currentStep}`
      )}
    >
      {currentStep === 0 && (
        <div className="absolute inset-0">
          {isVariation ? (
            <VideoBackground
              className={"h-full w-full object-cover"}
              cdnFilename="silent-vr-trial"
              breakpointWidth={768}
            ></VideoBackground>
          ) : (
            <>
              <picture className={"h-full w-full object-cover"}>
                <source
                  className={"h-full w-full object-cover object-top"}
                  media="(min-aspect-ratio: 1)"
                  srcSet={
                    process.env.PUBLIC_URL +
                    "/vr_trial_background_landscape_a.jpg"
                  }
                />
                <img
                  className={"h-full w-full object-cover object-top"}
                  src={process.env.PUBLIC_URL + "/vr_trial_background_a.jpg"}
                  alt="Liteboxer VR fighter"
                />
              </picture>
              <div
                className={cx(
                  "absolute inset-0 mix-blend-multiply",
                  styles.backgroundGrad
                )}
              />
            </>
          )}
        </div>
      )}
      <Logo inverted={false} />

      <div className="min-h-screen flex px-2 sm:px-6 lg:px-8 max-w-2xl md:mx-auto items-center justify-center">
        <div className="mx-auto relative w-full pb-6">
          <StepContent visibleStep={0}>
            <div className="px-2">
              <Step0Form />
            </div>
          </StepContent>
          <StepContent visibleStep={1}>
            <div className="px-2">
              <Step1Form />
            </div>
          </StepContent>
          <StepContent visibleStep={2}>
            <Step2Form />
          </StepContent>
        </div>
      </div>
    </div>
  );
};

export const TrialExistingUser = () => {
  const token = useStoreToken();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (window.analytics) {
      window.analytics.page("Members - VR Trial - Existing User", {
        source: "memberPortal",
        fbp: GetFBPcookie(),
      });
    }
  }, []);

  const Step0Form = () => {
    const initialFormValues = {
      email: "",
    };
    const {
      register,
      handleSubmit,
      setError,
      formState: { errors },
    } = useForm<TrialExistingMemberFormData>({
      defaultValues: { ...initialFormValues },
    });

    const onSubmit: SubmitHandler<TrialExistingMemberFormData> = (
      data: TrialExistingMemberFormData
    ) => {
      return verifyUserByEmail(data.email).then((response) => {
        if (response.ok) {
          response.json().then((success: VerifySuccess) => {
            if (window.analytics) {
              window.analytics.identify(success.uuid, {
                email: data.email,
                source: "memberPortal",
              });
            }
            addToCart(token, 0, 6).then((cart: Cart) => {
              const state: CheckoutState = {
                cart: cart,
                email: data.email,
                acceptsMarketing: false,
                createAccount: false,
                sourceGroup: CatalogGroups.VR,
              };

              navigate(`/checkout`, {
                state: state,
              });
            });
          });
        } else if (response.status === 404) {
          setError("email", {
            type: "server",
            message:
              "Profile not found. Please double-check that the email entered matches the email in your Liteboxer VR Profile.",
          });
        } else {
          setError("email", {
            type: "server",
            message:
              "Unable to confirm your account. Please contact support@liteboxer.com for further assistance.",
          });
        }
      });
    };

    return (
      <>
        <div className="flex">
          <div className="w-5/6 mx-auto">
            <h2 className="uppercase font-black tracking-wide text-3xl text-center text-white mt-6">
              Join Liteboxer. $9.99/mo for 3 Months.
            </h2>
            <h4 className="text-lg leading-7 text-white mt-4 mb-10 text-center">
              Real trainers, unlimited workouts, hit music, and more.
              <br className="md:hidden" /> Cancel anytime.
            </h4>
          </div>
        </div>
        <div className="flex">
          <div className="w-5/6 mx-auto">
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className={"mt-5"}>
                <Input
                  key="step0email"
                  className="bg-opacity-75 bg-white"
                  isError={!!errors.email}
                  label={"Email"}
                  placeholder={"Enter your email"}
                  showLabel={true}
                  id={"email"}
                  {...register("email", { required: true })}
                />
              </div>
              {errors.email && errors.email.type === "required" && (
                <InputError id="email-error">This field is required</InputError>
              )}
              {errors.email && errors.email.type === "server" && (
                <InputError id="email-error">
                  {errors.email.message ?? "Unknown error"}
                </InputError>
              )}

              <div className="w-64 mx-auto">
                <button
                  className={cx(
                    TRIAL_BUTTON_CLASSES,
                    "mt-6 tracking-wider py-3"
                  )}
                >
                  Get Started
                </button>
              </div>
            </form>
          </div>
        </div>
        <div className="text-center italic text-white text-xs mt-6">
          * Meta Quest headset sold separately via Meta. Offer valid for new
          members only.
        </div>
      </>
    );
  };

  return (
    <div
      className={cx(
        "min-h-screen bg-black relative",
        styles.container,
        `step-0`
      )}
    >
      <div className="absolute inset-0">
        <VideoBackground
          className={"h-full w-full object-cover"}
          cdnFilename="silent-vr-trial"
          breakpointWidth={768}
        ></VideoBackground>
      </div>
      <Logo inverted={false} />

      <div className="min-h-screen flex px-2 sm:px-6 lg:px-8 max-w-2xl md:mx-auto items-center justify-center">
        <div className="mx-auto relative w-full pb-6">
          <div className="px-2">
            <Step0Form />
          </div>
        </div>
      </div>
    </div>
  );
};
